export type ShowRoomImage = {
  id?: string;
  url: string;
  order?: number;
};

export type ZipFileEntry = {
  path: string;
  data: Uint8Array;
};

export const sanitizeFileName = (value?: string) =>
  (value || 'showroom')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^\.+/, '') || 'showroom';

export const getImageFileName = (image: ShowRoomImage, index: number) => {
  const urlPath = image.url.split('?')[0];
  const urlFileName = decodeURIComponent(urlPath.substring(urlPath.lastIndexOf('/') + 1));
  const fallbackName = image.id ? `${image.id}.jpg` : `image-${index + 1}.jpg`;
  return sanitizeFileName(urlFileName || fallbackName);
};

const makeCrc32Table = () => {
  const table = new Uint32Array(256);

  for (let i = 0; i < 256; i += 1) {
    let crc = i;

    for (let j = 0; j < 8; j += 1) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }

    table[i] = crc >>> 0;
  }

  return table;
};

const crc32Table = makeCrc32Table();

const getCrc32 = (data: Uint8Array) => {
  let crc = 0xffffffff;

  for (let i = 0; i < data.length; i += 1) {
    crc = crc32Table[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const pushUint16 = (target: number[], value: number) => {
  target.push(value & 0xff, (value >>> 8) & 0xff);
};

const pushUint32 = (target: number[], value: number) => {
  target.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
};

export const createZipBlob = (files: ZipFileEntry[]) => {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const centralDirectory: number[] = [];
  let offset = 0;

  files.forEach((file) => {
    const fileName = encoder.encode(file.path);
    const crc = getCrc32(file.data);
    const localHeader: number[] = [];

    pushUint32(localHeader, 0x04034b50);
    pushUint16(localHeader, 20);
    pushUint16(localHeader, 0x0800);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, 0);
    pushUint16(localHeader, 0);
    pushUint32(localHeader, crc);
    pushUint32(localHeader, file.data.length);
    pushUint32(localHeader, file.data.length);
    pushUint16(localHeader, fileName.length);
    pushUint16(localHeader, 0);

    chunks.push(new Uint8Array(localHeader), fileName, file.data);

    pushUint32(centralDirectory, 0x02014b50);
    pushUint16(centralDirectory, 20);
    pushUint16(centralDirectory, 20);
    pushUint16(centralDirectory, 0x0800);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint32(centralDirectory, crc);
    pushUint32(centralDirectory, file.data.length);
    pushUint32(centralDirectory, file.data.length);
    pushUint16(centralDirectory, fileName.length);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint16(centralDirectory, 0);
    pushUint32(centralDirectory, 0);
    pushUint32(centralDirectory, offset);
    centralDirectory.push(...Array.from(fileName));

    offset += localHeader.length + fileName.length + file.data.length;
  });

  const centralDirectoryOffset = offset;
  const centralDirectoryBytes = new Uint8Array(centralDirectory);
  chunks.push(centralDirectoryBytes);
  offset += centralDirectoryBytes.length;

  const endOfCentralDirectory: number[] = [];
  pushUint32(endOfCentralDirectory, 0x06054b50);
  pushUint16(endOfCentralDirectory, 0);
  pushUint16(endOfCentralDirectory, 0);
  pushUint16(endOfCentralDirectory, files.length);
  pushUint16(endOfCentralDirectory, files.length);
  pushUint32(endOfCentralDirectory, centralDirectoryBytes.length);
  pushUint32(endOfCentralDirectory, centralDirectoryOffset);
  pushUint16(endOfCentralDirectory, 0);
  chunks.push(new Uint8Array(endOfCentralDirectory));

  const blobParts = chunks.map((chunk) => {
    const buffer = new ArrayBuffer(chunk.byteLength);
    new Uint8Array(buffer).set(chunk);
    return buffer;
  });

  return new Blob(blobParts, { type: 'application/zip' });
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
